import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarCheck2, CalendarX } from "lucide-react";
import { listBookingsForGrid, listBlackoutsForGrid } from "@/lib/api";

const HORIZON_DAYS = 28; // preview window: next 4 weeks

/**
 * Expand a recurring-pattern definition into ISO occurrence ranges
 * over [from, to]. `from` and `to` are JS Date objects.
 */
export function expandPatternOccurrences(p, from, to) {
  if (!p?.start_date || !p?.time_start || !p?.time_end) return [];
  const startDate = new Date(`${p.start_date}T00:00:00`);
  const endDate = p.end_date ? new Date(`${p.end_date}T23:59:59`) : null;
  const lower = from > startDate ? from : startDate;
  const upper = endDate && endDate < to ? endDate : to;
  if (lower > upper) return [];

  const occs = [];
  const day = new Date(lower);
  day.setHours(0, 0, 0, 0);
  while (day <= upper) {
    if (matchesPattern(p, day, startDate)) {
      const [sh, sm] = p.time_start.split(":").map(Number);
      const [eh, em] = p.time_end.split(":").map(Number);
      const start = new Date(day);
      start.setHours(sh, sm, 0, 0);
      const end = new Date(day);
      end.setHours(eh, em, 0, 0);
      if (end > lower && start < upper) {
        occs.push({ start, end });
      }
    }
    day.setDate(day.getDate() + 1);
  }
  return occs;
}

function matchesPattern(p, day, startDate) {
  if (p.pattern_type === "daily") return true;
  if (p.pattern_type === "weekly") {
    return (p.day_of_week || []).includes(day.getDay());
  }
  if (p.pattern_type === "fortnightly") {
    if (!(p.day_of_week || []).includes(day.getDay())) return false;
    const ms = day.getTime() - startDate.getTime();
    const weeks = Math.floor(ms / (7 * 24 * 3600 * 1000));
    return weeks % 2 === 0;
  }
  if (p.pattern_type === "monthly") {
    return day.getDate() === startDate.getDate();
  }
  return false;
}

export default function PatternClashPreview({ values, bays }) {
  const [bookings, setBookings] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [loading, setLoading] = useState(false);

  const from = useMemo(() => {
    const d = new Date(`${values.start_date}T00:00:00`);
    const now = new Date();
    return d > now ? d : now;
  }, [values.start_date]);
  const to = useMemo(() => {
    const d = new Date(from);
    d.setDate(d.getDate() + HORIZON_DAYS);
    if (values.end_date) {
      const ed = new Date(`${values.end_date}T23:59:59`);
      if (ed < d) return ed;
    }
    return d;
  }, [from, values.end_date]);

  useEffect(() => {
    let cancelled = false;
    if (!values.site_id) return;
    const fromISO = from.toISOString();
    const toISO = to.toISOString();
    (async () => {
      const [bk, bo] = await Promise.all([
        listBookingsForGrid(values.site_id, fromISO, toISO),
        listBlackoutsForGrid(values.site_id, fromISO, toISO),
      ]);
      if (cancelled) return;
      setBookings((bk.data || []).filter((b) => b.bay_id === values.bay_id));
      setBlackouts(
        (bo.data || []).filter((b) => !b.bay_id || b.bay_id === values.bay_id)
      );
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [values.site_id, values.bay_id, from, to]);

  const occurrences = useMemo(
    () => expandPatternOccurrences(values, from, to),
    [values, from, to]
  );

  const annotated = useMemo(() => {
    return occurrences.map((o) => {
      const clashBooking = bookings.find(
        (b) => new Date(b.scheduled_start) < o.end && new Date(b.scheduled_end) > o.start
      );
      const clashBlackout = blackouts.find(
        (b) => new Date(b.start_time) < o.end && new Date(b.end_time) > o.start
      );
      return { ...o, clashBooking, clashBlackout };
    });
  }, [occurrences, bookings, blackouts]);

  const bay = bays.find((b) => b.id === values.bay_id);

  if (!values.site_id || !values.bay_id) {
    return (
      <div className="rounded-md border border-dashed py-8 text-center text-sm text-slate-400">
        Pick a site and bay to preview occurrences.
      </div>
    );
  }

  const clashCount = annotated.filter((a) => a.clashBooking || a.clashBlackout).length;

  return (
    <div className="space-y-2" data-testid="pattern-clash-preview">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          Next <strong>{HORIZON_DAYS}</strong> days · {bay?.name || ""} ·{" "}
          <strong>{annotated.length}</strong> occurrence{annotated.length === 1 ? "" : "s"}
        </span>
        {clashCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid="pattern-clash-count">
            <AlertTriangle className="h-3.5 w-3.5" /> {clashCount} clash
            {clashCount === 1 ? "" : "es"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CalendarCheck2 className="h-3.5 w-3.5" /> No clashes
          </span>
        )}
      </div>

      <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
        ) : annotated.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">
            No occurrences in the next {HORIZON_DAYS} days.
          </div>
        ) : (
          annotated.map((a, i) => {
            const clash = a.clashBooking || a.clashBlackout;
            return (
              <div
                key={i}
                className={`flex items-center gap-3 px-3 py-2 text-sm ${
                  clash ? "bg-amber-50" : ""
                }`}
                data-testid={`pattern-occ-${i}`}
              >
                <span className="text-slate-500 tabular-nums w-44 shrink-0">
                  {fmtOcc(a.start, a.end)}
                </span>
                {clash ? (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-800">
                    <CalendarX className="h-3.5 w-3.5" />
                    {a.clashBooking
                      ? `Clashes with booking ${a.clashBooking.reference_number}`
                      : `Clashes with blackout "${a.clashBlackout.title || a.clashBlackout.reason}"`}
                  </span>
                ) : (
                  <span className="text-xs text-emerald-700">Available</span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function fmtOcc(s, e) {
  const d = s.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  const fmtT = (x) => `${pad(x.getHours())}:${pad(x.getMinutes())}`;
  return `${d}  ${fmtT(s)}–${fmtT(e)}`;
}
function pad(n) {
  return String(n).padStart(2, "0");
}
